import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, CalendarPlus, CheckCircle2, Clock, Mail, Plus, RefreshCw, Video } from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate, fmtDateTime, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StatusBadge, TableSkeleton } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SelectField } from "@/components/forms/Fields";
import { FeedbackForm, InterviewForm } from "@/components/forms/OpsForms";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormGrid, TextAreaField, TextField } from "@/components/forms/Fields";

function RescheduleDialog({ interview, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open || !interview) return;
    const local = new Date(interview.scheduled_at);
    const offset = local.getTime() - local.getTimezoneOffset() * 60000;
    setForm({
      scheduled_at: new Date(offset).toISOString().slice(0, 16),
      duration_minutes: interview.duration_minutes || 45,
      meeting_link: interview.meeting_link || "",
      reason: "",
    });
  }, [open, interview]);

  const save = async () => {
    try {
      await api.patch(`/interviews/${interview.id}/reschedule`, {
        ...form, scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 45,
      });
      toast.success("Rescheduled — updated invites sent");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Dialog open={open && !!interview} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-slate-200 bg-white">
        <DialogHeader>
          <DialogTitle className="text-base text-slate-900">Reschedule interview</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-500">
            {interview?.candidate_name} · {interview?.job_title}. An updated calendar invite goes to the
            candidate, the panel and the recruiter, and reminders are re-armed for the new time.
          </p>
          <FormGrid>
            <TextField label="New date &amp; time" type="datetime-local" value={form.scheduled_at}
              onChange={(v) => setForm((f) => ({ ...f, scheduled_at: v }))} testid="reschedule-datetime-input" />
            <TextField label="Duration (mins)" type="number" value={form.duration_minutes}
              onChange={(v) => setForm((f) => ({ ...f, duration_minutes: v }))} />
          </FormGrid>
          <TextField label="Meeting link" value={form.meeting_link} placeholder="https://meet…"
            onChange={(v) => setForm((f) => ({ ...f, meeting_link: v }))} />
          <TextAreaField label="Reason (internal)" rows={2} value={form.reason}
            onChange={(v) => setForm((f) => ({ ...f, reason: v }))} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={save} data-testid="reschedule-submit-button" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            Reschedule &amp; resend invites
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function dayKey(iso) {
  return new Date(iso).toDateString();
}

export default function Interviews() {
  const { meta, isInternal, user } = useAuth();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [fb, setFb] = useState(null);
  const [rescheduling, setRescheduling] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    setData(null);
    try {
      const { data: res } = await api.get("/interviews", {
        params: { status: status !== "all" ? status : undefined, search: search || undefined },
      });
      setData(res);
    } catch (e) {
      toast.error(apiError(e));
      setData({ items: [] });
    }
  }, [status, search]);

  useEffect(() => {
    const t = setTimeout(load, 220);
    return () => clearTimeout(t);
  }, [load, refresh]);

  const grouped = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    data.items.forEach((iv) => {
      const k = dayKey(iv.scheduled_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(iv);
    });
    return [...map.entries()];
  }, [data]);

  const notifyCandidate = async (id) => {
    try {
      const { data } = await api.post(`/interviews/${id}/invites`);
      if (data.delivered) toast.success(`Calendar invites sent to ${data.sent.length} recipient(s)`);
      else toast.error(data.message || "Invites could not be delivered right now");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setStatusFor = async (id, value) => {
    try {
      await api.patch(`/interviews/${id}/status`, { status: value });
      toast.success(`Marked as ${value}`);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Scheduling"
        title="Interviews"
        subtitle="Every round across clients and panels, with structured feedback capture."
        actions={
          isInternal &&
          user?.role !== "interviewer" && (
            <Button onClick={() => setOpen(true)} data-testid="schedule-interview-cta" className="h-9 gap-2 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500">
              <Plus className="h-3.5 w-3.5" /> Schedule interview
            </Button>
          )
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="interviews-search-input"
          placeholder="Search by candidate or job"
          className="h-9 max-w-sm border-slate-200 bg-slate-50 text-sm text-slate-800 placeholder:text-slate-400" />
        <SelectField value={status} onChange={setStatus} allowAll options={["Scheduled", "Completed", "Cancelled", "No Show", "Rescheduled"]}
          testid="interviews-status-filter" className="w-48" placeholder="Status" />
      </div>

      {!data ? (
        <TableSkeleton rows={6} cols={4} />
      ) : data.items.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No interviews found" description="Schedule a round from a candidate profile or the pipeline board." testid="interviews-empty-state" />
      ) : (
        <div className="space-y-8">
          {grouped.map(([day, items]) => (
            <section key={day}>
              <div className="mb-3 flex items-center gap-3">
                <p className="text-sm font-medium text-slate-800">
                  {new Date(day).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
                </p>
                <span className="font-mono-num rounded bg-slate-100 px-1.5 text-[10px] text-slate-500">{items.length}</span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="grid gap-3 lg:grid-cols-2">
                {items.map((iv) => (
                  <article key={iv.id} data-testid="interview-card" className="surface surface-hover rounded-md p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[15px] text-slate-900">{iv.candidate_name}</p>
                        <p className="truncate text-xs text-slate-500">
                          {iv.job_title} · {iv.client_name || "Internal"}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="font-mono-num text-sm text-slate-800">{fmtTime(iv.scheduled_at)}</p>
                        <p className="text-[10px] text-slate-400">{iv.duration_minutes} mins</p>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <StatusBadge value={iv.round_name} />
                      <StatusBadge value={iv.interview_type} />
                      <StatusBadge value={iv.status} />
                      {iv.feedback && <StatusBadge value={iv.feedback.recommendation} />}
                    </div>

                    <p className="mt-3 text-[11px] text-slate-500">
                      Panel: {iv.interviewer_name || "Not assigned"}
                    </p>
                    {iv.status === "Scheduled" && (
                      <p className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400" data-testid="reminder-status">
                        <Clock className="h-3 w-3" />
                        {iv.reminders_sent?.["1h"]
                          ? "1-hour reminder sent"
                          : iv.reminders_sent?.["24h"]
                            ? "24-hour reminder sent · 1-hour reminder pending"
                            : "Reminders scheduled 24h and 1h before"}
                      </p>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-200 pt-4">
                      {iv.meeting_link && (
                        <Button size="sm" variant="ghost" onClick={() => window.open(iv.meeting_link, "_blank")}
                          className="h-8 gap-1.5 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-100">
                          <Video className="h-3 w-3" /> Join
                        </Button>
                      )}
                      {iv.calendar_url && iv.status !== "Cancelled" && (
                        <Button size="sm" variant="ghost" onClick={() => window.open(iv.calendar_url, "_blank")}
                          data-testid="add-to-calendar-button"
                          className="h-8 gap-1.5 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-100">
                          <CalendarPlus className="h-3 w-3" /> Add to calendar
                        </Button>
                      )}
                      {!iv.feedback && (
                        <Button size="sm" variant="ghost" onClick={() => setFb(iv)} data-testid="add-feedback-button"
                          className="h-8 gap-1.5 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-100">
                          <CheckCircle2 className="h-3 w-3" /> Add feedback
                        </Button>
                      )}
                      {isInternal && user?.role !== "interviewer" && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => notifyCandidate(iv.id)} data-testid="notify-candidate-button"
                            className="h-8 gap-1.5 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-100">
                            <Mail className="h-3 w-3" /> Send invites
                          </Button>
                          {iv.status === "Scheduled" && (
                            <Button size="sm" variant="ghost" onClick={() => setRescheduling(iv)} data-testid="reschedule-button"
                              className="h-8 gap-1.5 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-100">
                              <RefreshCw className="h-3 w-3" /> Reschedule
                            </Button>
                          )}
                          {iv.status === "Scheduled" && (
                            <Button size="sm" variant="ghost" onClick={() => setStatusFor(iv.id, "Cancelled")}
                              className="h-8 rounded-full border border-rose-200 text-[11px] text-rose-700 hover:bg-rose-50">
                              Cancel
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <InterviewForm open={open} onOpenChange={setOpen} onSaved={() => setRefresh((r) => r + 1)} />
      <RescheduleDialog interview={rescheduling} open={!!rescheduling}
        onOpenChange={(v) => !v && setRescheduling(null)} onSaved={() => setRefresh((r) => r + 1)} />
      {fb && <FeedbackForm open={!!fb} onOpenChange={(v) => !v && setFb(null)} interview={fb} onSaved={() => setRefresh((r) => r + 1)} />}
    </div>
  );
}
